/**
 * Post-process an MST walkway network into ORDERLY axis-aligned 3'
 * corridors. The MST from `computeWalkwayNetwork` produces straight
 * edges between anchor points at arbitrary angles; for "reserved
 * walkway" use (zones cabinets must avoid) we want each corridor to
 * run parallel to one of the kitchen's primary wall directions, so
 * that intersections form 90° (or wall-determined-angle) junctions.
 *
 * Approach:
 *   1. Determine the kitchen's primary axes from its walls (typically
 *      X and Z; rotated for rotated kitchens). The most-mass wall
 *      angle (mod 90°) wins.
 *   2. For each MST edge:
 *        - if already aligned to a primary axis (within snap
 *          tolerance), emit one rectangle
 *        - else, emit an L-shape: two axis-aligned segments meeting
 *          at an internal 90° corner. The longer leg goes first.
 *   3. Each output rectangle is `2 * halfWidth` wide centred on the
 *      segment.
 *
 * Pure: takes a network + walls in, returns rectangles. No I/O.
 */

const { isPointInsidePolygon } = require('./reachableRegions');
const { doPolygonsOverlap } = require('../bathroom-analyzer/pipeline/geometry');

/** Does any rectangle overlap any obstacle (a counter the walkway avoids)? */
const hitsObstacle = (rects, obstacles) =>
  obstacles.length > 0 &&
  rects.some((r) => obstacles.some((o) => doPolygonsOverlap(r, o)));

/**
 * A network edge bent round obstacles (`path`: anchor, waypoints…, anchor)
 * becomes one straight edge per leg; every other edge is itself.
 */
function legsOf(edges) {
  return edges.flatMap((e) =>
    Array.isArray(e.path) && e.path.length > 2
      ? e.path
          .slice(0, -1)
          .map((p, i) => ({
            ...e,
            from: p,
            to: e.path[i + 1],
            path: null,
            leg: i,
          }))
      : [e]
  );
}

const FOOT = 0.3048;
const DEFAULT_HALF_WIDTH = 1.5 * FOOT; // 18" each side → 36" total
const DEFAULT_SNAP_TOLERANCE_DEG = 15;
// Corridor centerline farther than this from any wall counts as crossing the
// open interior (vs. hugging a wall). Drives routing-option selection.
const OPEN_INTERIOR_THRESHOLD = 0.6;

/**
 * Find the dominant wall direction (mod 90°) and return two
 * perpendicular unit-vectors aligned to it. Walls are weighted by
 * length so a long counter-wall dominates over a tiny notch.
 *
 * @param {Array<Object>} walls - room walls; each must expose
 *   `alignment.bottomLeft` and `alignment.bottomRight`.
 * @returns {[{x,z}, {x,z}]} primary, secondary unit axes.
 */
function computePrimaryAxes(walls) {
  const buckets = new Array(90).fill(0);
  let any = false;
  for (const wall of walls || []) {
    const a = wall?.alignment?.bottomLeft;
    const b = wall?.alignment?.bottomRight;
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.1) continue;
    let deg = (Math.atan2(dz, dx) * 180) / Math.PI;
    deg = ((deg % 90) + 90) % 90;
    const bucket = Math.round(deg) % 90;
    buckets[bucket] += len;
    any = true;
  }
  if (!any) {
    return [
      { x: 1, z: 0 },
      { x: 0, z: 1 },
    ];
  }
  let best = 0;
  let bestScore = -1;
  for (let i = 0; i < 90; i++) {
    if (buckets[i] > bestScore) {
      bestScore = buckets[i];
      best = i;
    }
  }
  const rad = (best * Math.PI) / 180;
  return [
    { x: Math.cos(rad), z: Math.sin(rad) },
    { x: -Math.sin(rad), z: Math.cos(rad) },
  ];
}

function dot2(a, b) {
  return a.x * b.x + a.z * b.z;
}

function add2(p, v, scale = 1) {
  return { x: p.x + v.x * scale, z: p.z + v.z * scale };
}

/**
 * Distinct projections of `polygon`'s vertices onto `axis` that lie strictly
 * between `lo` and `hi` (the two anchors' projections). These are the WALL
 * LINES a jog can align to so the corridor hugs a wall instead of crossing
 * the open interior. Sorted nearest-to-`lo` first for stable ordering.
 */
function wallLineCoords(polygon, axis, lo, hi) {
  const min = Math.min(lo, hi);
  const max = Math.max(lo, hi);
  const eps = 0.02;
  const seen = new Set();
  const out = [];
  for (const p of polygon) {
    const c = dot2(p, axis);
    if (c <= min + eps || c >= max - eps) continue;
    const key = c.toFixed(3);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  out.sort((a, b) => Math.abs(a - lo) - Math.abs(b - lo));
  return out;
}

function distPointToSegment2(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

function minDistToPolygonEdge(p, polygon) {
  let m = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const d = distPointToSegment2(
      p,
      polygon[i],
      polygon[(i + 1) % polygon.length]
    );
    if (d < m) m = d;
  }
  return m;
}

/**
 * Total corridor length (across the option's rectangles) whose centerline runs
 * farther than {@link OPEN_INTERIOR_THRESHOLD} from any polygon edge — i.e.
 * crossing the open middle of the room rather than hugging a wall. Lower is
 * less disruptive; `rectifyWalkways` picks the option that minimizes it.
 */
function openInteriorLength(rectangles, polygon) {
  let total = 0;
  for (const r of rectangles) {
    let x0 = Infinity;
    let x1 = -Infinity;
    let z0 = Infinity;
    let z1 = -Infinity;
    for (const p of r) {
      if (p.x < x0) x0 = p.x;
      if (p.x > x1) x1 = p.x;
      if (p.z < z0) z0 = p.z;
      if (p.z > z1) z1 = p.z;
    }
    const w = x1 - x0;
    const h = z1 - z0;
    const len = Math.max(w, h);
    if (len < 0.05) continue;
    const horiz = w >= h;
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    const steps = Math.max(2, Math.round(len / 0.15));
    for (let i = 0; i < steps; i++) {
      const f = (i + 0.5) / steps;
      const pt = horiz ? { x: x0 + f * w, z: cz } : { x: cx, z: z0 + f * h };
      if (minDistToPolygonEdge(pt, polygon) > OPEN_INTERIOR_THRESHOLD) {
        total += len / steps;
      }
    }
  }
  return total;
}

/**
 * Rectify a single MST edge into a list of axis-aligned routing
 * options. Each option is an array of segments. The caller validates each
 * against the polygon and scores them (least open-interior wins).
 *
 * Options generated:
 *   1. Single segment — ONLY when anchors are exactly axis-aligned
 *      (perpendicular component < 1cm). The centerline then passes
 *      through both anchors.
 *   2. L-shape, longer-leg-first, and shorter-leg-first.
 *   3. Wall-aligned jogs — a Z whose full cross-traverse sits on a polygon
 *      wall line (so the corridor follows a wall rather than the open middle).
 *   4. Fixed-fraction Z-shapes (jog near each end) as a fallback.
 *
 * @param {Array<{x, z}>} [polygon] - clip polygon; supplies the wall lines for
 *   option (3). Omitted → only L + fixed-fraction Z options are generated.
 */
function rectifyEdge(from, to, primaryAxes, polygon) {
  const vec = { x: to.x - from.x, z: to.z - from.z };
  const len = Math.hypot(vec.x, vec.z);
  if (len < 1e-6) return [];
  const c1 = dot2(vec, primaryAxes[0]);
  const c2 = dot2(vec, primaryAxes[1]);

  // Exactly-aligned cases: a single segment along the dominant axis,
  // passing through both anchors. "Exact" = the perpendicular component
  // is below 1cm (well under construction tolerance); anything larger
  // gets the L-shape so each leg's centerline is on one anchor's coord.
  const ALIGNED_TOLERANCE = 0.01; // 1cm
  if (Math.abs(c2) < ALIGNED_TOLERANCE) {
    return [
      [
        {
          from,
          to,
          axisIndex: 0,
        },
      ],
    ];
  }
  if (Math.abs(c1) < ALIGNED_TOLERANCE) {
    return [
      [
        {
          from,
          to,
          axisIndex: 1,
        },
      ],
    ];
  }

  // L-shape with both legs. Each leg's centerline passes through one
  // anchor's coordinate (the corner is at the cross of those coords),
  // so neither anchor is offset from a corridor.
  const turnA = add2(from, primaryAxes[0], c1);
  const turnB = add2(from, primaryAxes[1], c2);
  const lAxis1First = [
    { from, to: turnA, axisIndex: 0 },
    { from: turnA, to, axisIndex: 1 },
  ];
  const lAxis2First = [
    { from, to: turnB, axisIndex: 1 },
    { from: turnB, to, axisIndex: 0 },
  ];
  const longerFirst = Math.abs(c1) >= Math.abs(c2) ? lAxis1First : lAxis2First;
  const shorterFirst = Math.abs(c1) >= Math.abs(c2) ? lAxis2First : lAxis1First;
  const lOptions =
    shorterFirst === longerFirst ? [longerFirst] : [longerFirst, shorterFirst];

  // Wall-aligned jogs: put the FULL cross-axis traverse on a polygon wall
  // line (a vertex coordinate between the two anchors), so the route follows
  // a wall instead of jogging at an arbitrary fraction. This is what lets a
  // slanted edge across a narrowing room hug the perimeter. Each is the same
  // Z construction as below, but with the jog fraction derived from the wall
  // line rather than a fixed 0.15 / 0.85.
  const wallZOptions = [];
  if (polygon && polygon.length >= 3) {
    const f0 = dot2(from, primaryAxes[0]);
    const f1 = dot2(from, primaryAxes[1]);
    for (const u of wallLineCoords(polygon, primaryAxes[0], f0, f0 + c1)) {
      const p1 = add2(from, primaryAxes[0], u - f0); // full axis1 traverse at u
      const p2 = add2(p1, primaryAxes[1], c2);
      wallZOptions.push([
        { from, to: p1, axisIndex: 0 },
        { from: p1, to: p2, axisIndex: 1 },
        { from: p2, to, axisIndex: 0 },
      ]);
    }
    for (const v of wallLineCoords(polygon, primaryAxes[1], f1, f1 + c2)) {
      const p1 = add2(from, primaryAxes[1], v - f1); // full axis0 traverse at v
      const p2 = add2(p1, primaryAxes[0], c1);
      wallZOptions.push([
        { from, to: p1, axisIndex: 1 },
        { from: p1, to: p2, axisIndex: 0 },
        { from: p2, to, axisIndex: 1 },
      ]);
    }
  }

  // Z-shape options (3 segments, 2 turns) — tried after L-shapes;
  // used when an L can't fit inside the room polygon. A Z splits one
  // axis at fraction α and traverses the other axis fully in the
  // middle leg, giving more flexibility to route around concave
  // polygon corners (kitchens with a top notch + offset bottom bump
  // can't fit a straight L through both).
  //
  // Jog position: prefer jogs near one END or the other (α near 0 or 1)
  // rather than in the middle. A mid-room jog cuts the kitchen body in
  // half visually; a jog hugging one end keeps the trunk + the jog
  // tucked along the side, leaving most of the kitchen body
  // unobstructed for cabinetry. Two split fractions only: one near
  // each end of the slanted MST edge.
  const zOptions = [];
  const splits = [0.15, 0.85];
  for (const alpha of splits) {
    // Z-axis1-first: along axis1 by α·c1, then along axis2 by c2,
    // then along axis1 by (1-α)·c1.
    const p1a = add2(from, primaryAxes[0], alpha * c1);
    const p2a = add2(p1a, primaryAxes[1], c2);
    zOptions.push([
      { from, to: p1a, axisIndex: 0 },
      { from: p1a, to: p2a, axisIndex: 1 },
      { from: p2a, to, axisIndex: 0 },
    ]);
    // Z-axis2-first: along axis2 by α·c2, then along axis1 by c1,
    // then along axis2 by (1-α)·c2.
    const p1b = add2(from, primaryAxes[1], alpha * c2);
    const p2b = add2(p1b, primaryAxes[0], c1);
    zOptions.push([
      { from, to: p1b, axisIndex: 1 },
      { from: p1b, to: p2b, axisIndex: 0 },
      { from: p2b, to, axisIndex: 1 },
    ]);
  }

  return [...lOptions, ...wallZOptions, ...zOptions];
}

function snapOntoAxisLine(point, lineThrough, axisDir) {
  const dx = point.x - lineThrough.x;
  const dz = point.z - lineThrough.z;
  const t = dx * axisDir.x + dz * axisDir.z;
  return {
    x: lineThrough.x + t * axisDir.x,
    z: lineThrough.z + t * axisDir.z,
  };
}

/**
 * Parametric `t` along a ray (origin + t*dir) where it hits the
 * line segment a→b. Negative `t` means the hit is behind the origin;
 * caller filters to positive hits. `null` when ray and segment are
 * parallel or the segment isn't crossed.
 */
/**
 * True if `point` lies inside the axis-aligned bounding box of `rect`
 * with a small tolerance. The rect may be slightly non-axis-aligned
 * (e.g. the slanted-fallback case); we just need to know the point is
 * within halfWidth of the corridor centerline, which the bounding-box
 * check approximates well for our use.
 */
function pointInsideAxisAlignedRect(point, rect, halfWidth) {
  let xMin = Infinity;
  let xMax = -Infinity;
  let zMin = Infinity;
  let zMax = -Infinity;
  for (const v of rect) {
    if (v.x < xMin) xMin = v.x;
    if (v.x > xMax) xMax = v.x;
    if (v.z < zMin) zMin = v.z;
    if (v.z > zMax) zMax = v.z;
  }
  const eps = 1e-3;
  return (
    point.x >= xMin - eps &&
    point.x <= xMax + eps &&
    point.z >= zMin - eps &&
    point.z <= zMax + eps
  );
}

/**
 * Distance from `origin` along unit direction `dir` to the first
 * polygon edge a forward ray intersects, or `null` if none. Used to
 * cap segment-direction extensions at the polygon boundary.
 */
function rayDistanceToPolygon(origin, dir, polygon) {
  let minT = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const t = rayLineSegmentT(origin, dir, a, b);
    if (t !== null && t > 1e-9 && t < minT) minT = t;
  }
  return minT === Infinity ? null : minT;
}

function rayLineSegmentT(origin, dir, a, b) {
  const sx = b.x - a.x;
  const sz = b.z - a.z;
  const denom = dir.x * sz - dir.z * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((a.x - origin.x) * sz - (a.z - origin.z) * sx) / denom;
  const s = ((a.x - origin.x) * dir.z - (a.z - origin.z) * dir.x) / denom;
  if (s < -1e-9 || s > 1 + 1e-9) return null;
  return t;
}

/**
 * Polygon's n-coverage at a u-sample on the centerline as a single
 * interval [low, high] (signed n-units, relative to `c`):
 *   - `c` INSIDE polygon → containing interval (first +n exit, first
 *     -n exit).
 *   - `c` OUTSIDE polygon (e.g. across a wall) → the nearest polygon-
 *     interior strip on either side, picked by closeness to 0.
 * `null` when no usable interval is found along the line through `c`.
 *
 * For non-convex polygons with multiple disjoint interiors along this
 * line, only the closest one is returned — sufficient for typical
 * kitchen polygons; would need a multi-interval treatment for U-shaped
 * rooms with the U's mouth crossing the corridor.
 */
function polygonNCoverageAtSample(c, n, polygon) {
  const tsPos = [];
  const tsNeg = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const tp = rayLineSegmentT(c, n, a, b);
    if (tp !== null && tp > 1e-9) tsPos.push(tp);
    const tn = rayLineSegmentT(c, { x: -n.x, z: -n.z }, a, b);
    if (tn !== null && tn > 1e-9) tsNeg.push(tn);
  }
  tsPos.sort((a, b) => a - b);
  tsNeg.sort((a, b) => a - b);
  if (isPointInsidePolygon(c, polygon)) {
    if (tsPos.length === 0 || tsNeg.length === 0) return null;
    return { low: -tsNeg[0], high: tsPos[0] };
  }
  const posSpan = tsPos.length >= 2 ? { low: tsPos[0], high: tsPos[1] } : null;
  const negSpan =
    tsNeg.length >= 2 ? { low: -tsNeg[1], high: -tsNeg[0] } : null;
  if (!posSpan && !negSpan) return null;
  if (!posSpan) return negSpan;
  if (!negSpan) return posSpan;
  return Math.abs(posSpan.low) <= Math.abs(negSpan.high) ? posSpan : negSpan;
}

/**
 * Shift a rectangle perpendicular to its centerline so the full
 * 2*halfWidth-wide strip fits inside the polygon. At every u-sample
 * along the (extended) centerline, compute the polygon's n-coverage
 * interval [low, high] (works whether the centerline sample is inside
 * or outside the polygon — see `polygonNCoverageAtSample`), translate
 * to allowed shift Δ ∈ [low+halfWidth, high-halfWidth], and intersect
 * across all samples. Pick the smallest |Δ| in the final intersection.
 *
 * Returns:
 *   - shifted rectangle, when a non-zero Δ is needed
 *   - original rectangle when Δ = 0 fits
 *   - `null` when the intersection is empty (corridor can't fit a 3 ft
 *     walkway anywhere along its length, e.g. the room narrows below
 *     3 ft, or one end's walls pull the corridor in opposing directions
 *     to the other end's).
 */
function shiftRectangleIntoPolygon(rectangle, segment, halfWidth, polygon) {
  const { from, to } = segment;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return rectangle;
  const ux = dx / len;
  const uz = dz / len;
  const nx = -uz;
  const nz = ux;
  // Sample along the rectangle's ACTUAL u-extent (which may be shorter
  // than from-halfWidth..to+halfWidth if adaptive extension truncated
  // the rectangle at a polygon boundary). Project all 4 vertices onto
  // u to find the extent endpoints.
  let uMin = Infinity;
  let uMax = -Infinity;
  for (const v of rectangle) {
    const proj = (v.x - from.x) * ux + (v.z - from.z) * uz;
    if (proj < uMin) uMin = proj;
    if (proj > uMax) uMax = proj;
  }
  const eFromX = from.x + ux * uMin;
  const eFromZ = from.z + uz * uMin;
  const segLength = uMax - uMin;
  const sampleCount = Math.max(8, Math.ceil(segLength / 0.1));

  let allowedLow = -Infinity;
  let allowedHigh = Infinity;

  for (let i = 0; i <= sampleCount; i++) {
    const t = i / sampleCount;
    const c = {
      x: eFromX + t * segLength * ux,
      z: eFromZ + t * segLength * uz,
    };
    const range = polygonNCoverageAtSample(c, { x: nx, z: nz }, polygon);
    if (!range) continue;
    const lo = range.low + halfWidth;
    const hi = range.high - halfWidth;
    if (lo > hi) return null;
    if (lo > allowedLow) allowedLow = lo;
    if (hi < allowedHigh) allowedHigh = hi;
    if (allowedLow > allowedHigh) return null;
  }

  if (!isFinite(allowedLow) || !isFinite(allowedHigh)) return rectangle;
  let delta;
  if (allowedLow > 0) delta = allowedLow;
  else if (allowedHigh < 0) delta = allowedHigh;
  else delta = 0;
  if (Math.abs(delta) < 1e-9) return rectangle;
  return rectangle.map((v) => ({
    x: v.x + delta * nx,
    z: v.z + delta * nz,
  }));
}

/**
 * Walk outward from `endpoint` along `dir` (unit vector) at small
 * steps. At each step, the rectangle's perpendicular extent at that
 * point is `[centerline ± halfWidth * normal]`. Find the maximum
 * extension distance such that BOTH perpendicular endpoints stay
 * inside the polygon.
 *
 * Returns a value in `[0, maxExtension]`. `maxExtension` is the
 * caller's already-computed upper bound (e.g., the centerline's
 * along-segment-direction distance to polygon boundary).
 */
function maxExtensionWithPerpFit(
  endpoint,
  dir,
  normal,
  halfWidth,
  maxExtension,
  polygon
) {
  if (maxExtension <= 0 || !polygon) return Math.max(0, maxExtension);
  const stepCount = 16; // 16 sub-samples → ~1cm steps for 18" extension
  let validExt = 0;
  for (let i = 0; i <= stepCount; i++) {
    const ext = (i / stepCount) * maxExtension;
    const cx = endpoint.x + ext * dir.x;
    const cz = endpoint.z + ext * dir.z;
    const left = { x: cx + normal.x * halfWidth, z: cz + normal.z * halfWidth };
    const right = {
      x: cx - normal.x * halfWidth,
      z: cz - normal.z * halfWidth,
    };
    if (
      isPointInsidePolygon(left, polygon) &&
      isPointInsidePolygon(right, polygon)
    ) {
      validExt = ext;
    } else {
      break; // first sample that fails — stop, don't extend further
    }
  }
  return validExt;
}

/**
 * Convert a (from, to) segment into a 4-corner rectangle 2 *
 * halfWidth wide, perpendicular to the segment. The rectangle is
 * EXTENDED past each endpoint by up to `halfWidth` along the segment
 * direction so that two perpendicular rectangles meeting at a shared
 * endpoint (an L-corner or anchor hub) overlap in a full
 * (2*halfWidth) × (2*halfWidth) square. When `polygon` is provided,
 * each extension is capped at the polygon's boundary along the
 * segment direction — keeps the rectangle from poking past walls at
 * tight L-turns. A second pass then caps the extension further based
 * on perpendicular fit: the rectangle corners at the extended endpoint
 * must also stay inside the polygon. CCW order for our X-Z convention.
 */
function segmentToRectangle(segment, halfWidth = DEFAULT_HALF_WIDTH, polygon) {
  const { from, to } = segment;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  const ux = dx / len;
  const uz = dz / len;
  const nx = -uz;
  const nz = ux;
  let extFrom = halfWidth;
  let extTo = halfWidth;
  if (polygon && polygon.length >= 3) {
    const distFrom = rayDistanceToPolygon(from, { x: -ux, z: -uz }, polygon);
    if (distFrom !== null && distFrom < halfWidth) {
      extFrom = Math.max(0, distFrom - 1e-4);
    }
    const distTo = rayDistanceToPolygon(to, { x: ux, z: uz }, polygon);
    if (distTo !== null && distTo < halfWidth) {
      extTo = Math.max(0, distTo - 1e-4);
    }
  }
  if (polygon && polygon.length >= 3) {
    extFrom = maxExtensionWithPerpFit(
      from,
      { x: -ux, z: -uz },
      { x: nx, z: nz },
      halfWidth,
      extFrom,
      polygon
    );
    extTo = maxExtensionWithPerpFit(
      to,
      { x: ux, z: uz },
      { x: nx, z: nz },
      halfWidth,
      extTo,
      polygon
    );
  }
  const eFrom = {
    x: from.x - ux * extFrom,
    z: from.z - uz * extFrom,
  };
  const eTo = {
    x: to.x + ux * extTo,
    z: to.z + uz * extTo,
  };
  // Winding matches `clipPolygonByConvex`'s expectation (same as
  // `bboxToPolygon` in sectionPolygons.js): positive shoelace in our
  // X-Z coords, so the half-plane "left of directed edge a→b" picks
  // out the rectangle interior.
  return [
    { x: eFrom.x - nx * halfWidth, z: eFrom.z - nz * halfWidth },
    { x: eTo.x - nx * halfWidth, z: eTo.z - nz * halfWidth },
    { x: eTo.x + nx * halfWidth, z: eTo.z + nz * halfWidth },
    { x: eFrom.x + nx * halfWidth, z: eFrom.z + nz * halfWidth },
  ];
}

/**
 * Main entry point: rectify every edge of an MST walkway network
 * against the kitchen's primary wall directions.
 *
 * @param {{edges: Array<{from, to}>}} network - output of
 *   `computeWalkwayNetwork`.
 * @param {Array<Object>} walls - room walls used to derive axes.
 * @param {Object} [options]
 * @param {number} [options.halfWidth=18"] - rectangle half-width.
 * @param {number} [options.snapToleranceDeg=15]
 * @param {[{x,z},{x,z}]} [options.primaryAxes] - override auto-detected
 *   axes (e.g. force X / Z when caller already knows the room is
 *   axis-aligned).
 * @param {Array<{x,z}>} [options.clipPolygon] - if provided, every
 *   rectangle is SHIFTED perpendicular to its centerline so the full
 *   2*halfWidth corridor fits inside this polygon (typically the
 *   room's floor polygon). When the polygon is narrower than the
 *   corridor at some point along its length, that rectangle is
 *   dropped — there's no valid 3'-wide alignment.
 * @returns {{
 *   primaryAxes: [{x,z},{x,z}],
 *   segments: Array<{from, to, axisIndex}>,
 *   rectangles: Array<Array<{x,z}>>
 * }}
 */
function rectifyWalkways(network, walls, options = {}) {
  const halfWidth = options.halfWidth ?? DEFAULT_HALF_WIDTH;
  const axes = options.primaryAxes || computePrimaryAxes(walls);
  const clipPoly = options.clipPolygon;
  const obstacles = options.obstacles || [];

  const segments = [];
  const rectangles = [];

  // Try one routing option (an array of axis-aligned sub-segments).
  // All sub-segments must produce a non-null shifted rectangle;
  // otherwise the option fails and the caller falls back to the swap.
  // The MST anchors (original edge endpoints) must be covered by SOME
  // rectangle in the chosen routing — otherwise the rectified walkway
  // doesn't actually connect the required points (it's been shifted
  // off the anchor). For each MST anchor, verify at least one rect
  // contains it.
  const anchorCovered = (rectsList, point) => {
    for (const { rect } of rectsList) {
      if (pointInsideAxisAlignedRect(point, rect, halfWidth)) return true;
    }
    return false;
  };

  // Consecutive sub-segments of an L or Z meet at a shared corner point
  // (`out[i-1].seg.to === out[i].seg.from`). The two rectangles overlap there
  // only while both still cover that corner. `shiftRectangleIntoPolygon`
  // moves each leg independently along its OWN normal, and an L's legs have
  // perpendicular normals — so a shift larger than halfWidth slides a leg off
  // its own corner and the corridor comes apart in the middle, while every
  // leg is individually valid and both MST anchors are still covered.
  //
  // A reserved walkway with a hole in it is worse than a slightly
  // worse-routed one, so reject the option and let the caller fall through to
  // the next routing (ultimately the slanted MST edge, connected by
  // construction).
  const cornersHeld = (out) => {
    for (let i = 1; i < out.length; i++) {
      const corner = out[i].seg.from;
      if (!pointInsideAxisAlignedRect(corner, out[i - 1].rect, halfWidth)) {
        return false;
      }
      if (!pointInsideAxisAlignedRect(corner, out[i].rect, halfWidth)) {
        return false;
      }
    }
    return true;
  };

  const tryOption = (subSegments, mstAnchors) => {
    if (!subSegments || subSegments.length === 0) return null;
    const out = [];
    for (const seg of subSegments) {
      const rect = segmentToRectangle(seg, halfWidth, clipPoly);
      if (!rect) return null;
      if (!clipPoly || clipPoly.length < 3) {
        out.push({ seg, rect });
        continue;
      }
      const shifted = shiftRectangleIntoPolygon(rect, seg, halfWidth, clipPoly);
      if (!shifted) return null;
      out.push({ seg, rect: shifted });
    }
    if (!cornersHeld(out)) return null;
    // A route through a counter is no route (the network kept its
    // centreline clear; a squared-off leg may still cut a corner).
    if (
      hitsObstacle(
        out.map(({ rect }) => rect),
        obstacles
      )
    )
      return null;
    if (mstAnchors) {
      for (const a of mstAnchors) {
        if (!anchorCovered(out, a)) return null;
      }
    }
    return out;
  };

  const edgeOptions = []; // edgeOptions[i] = Array<{ segments, rectangles }>
  const edges = legsOf(network?.edges || []);
  for (let edgeIndex = 0; edgeIndex < edges.length; edgeIndex++) {
    const edge = edges[edgeIndex];
    const opts = rectifyEdge(edge.from, edge.to, axes, clipPoly);

    // Collect ALL valid L-options for this edge.
    const validOptions = [];
    for (const opt of opts) {
      const r = tryOption(opt, [edge.from, edge.to]);
      if (r) validOptions.push(r);
    }

    if (validOptions.length === 0) {
      // Last resort: keep the slanted MST edge so connectivity is
      // preserved. Some kitchen polygons need a Z-shape route that
      // the L-only rectifier can't synthesize. Try shifting it first;
      // if even that fails, emit it unshifted — slightly stretching
      // outside the polygon at the extension caps is preferable to
      // dropping the corridor entirely.
      const slanted = [{ from: edge.from, to: edge.to, axisIndex: -1 }];
      let lastResort = tryOption(slanted, [edge.from, edge.to]);
      if (!lastResort) {
        const out = [];
        for (const seg of slanted) {
          const rect = segmentToRectangle(seg, halfWidth, clipPoly);
          if (rect) out.push({ seg, rect });
        }
        if (out.length) lastResort = out;
      }
      if (!lastResort) {
        edgeOptions.push([]);
        continue;
      }
      // Single last-resort option.
      const lrSegs = lastResort.map(({ seg }) => ({
        ...seg,
        edgeIndex,
        optionIndex: 0,
      }));
      const lrRects = lastResort.map(({ rect }) => rect);
      edgeOptions.push([{ segments: lrSegs, rectangles: lrRects }]);
      for (let k = 0; k < lrSegs.length; k++) {
        segments.push(lrSegs[k]);
        rectangles.push(lrRects[k]);
      }
      continue;
    }

    // Prefer the least-disruptive routing: sort the valid options so the one
    // whose corridor spends the least length crossing the open interior comes
    // first (it becomes the default output below). Ties keep the earlier
    // (L-first) option for stability. No polygon → keep first-fit order.
    if (clipPoly && clipPoly.length >= 3 && validOptions.length > 1) {
      const scored = validOptions.map((valid, i) => ({
        valid,
        i,
        score: openInteriorLength(
          valid.map(({ rect }) => rect),
          clipPoly
        ),
      }));
      scored.sort((a, b) => a.score - b.score || a.i - b.i);
      validOptions.length = 0;
      for (const e of scored) validOptions.push(e.valid);
    }

    // Build edgeOptions entries for all valid options, best-first.
    const optEntries = validOptions.map((valid, optionIndex) => {
      const segs = valid.map(({ seg }) => ({ ...seg, edgeIndex, optionIndex }));
      const rects = valid.map(({ rect }) => rect);
      return { segments: segs, rectangles: rects };
    });
    edgeOptions.push(optEntries);

    // Default output uses option 0 (primary L).
    for (let k = 0; k < optEntries[0].segments.length; k++) {
      segments.push(optEntries[0].segments[k]);
      rectangles.push(optEntries[0].rectangles[k]);
    }
  }
  return { primaryAxes: axes, segments, rectangles, edgeOptions };
}

/**
 * Compute the actual segment-direction extension endpoint at one
 * side of `seg` after `segmentToRectangle` would adapt extensions
 * against `polygon`. Returns an object `{ point, ext }` where:
 *   - `point` is the world-space endpoint that the rectangle's
 *     length actually reaches
 *   - `ext` is the distance from `seg.from` (or `seg.to`) along
 *     the outward direction.
 *
 * `which` is `'from'` or `'to'`.
 */
function extensionEndpoint(seg, halfWidth, polygon, which) {
  const dx = seg.to.x - seg.from.x;
  const dz = seg.to.z - seg.from.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) {
    const p = which === 'from' ? seg.from : seg.to;
    return { point: { x: p.x, z: p.z }, ext: 0 };
  }
  const ux = dx / len;
  const uz = dz / len;
  const nx = -uz;
  const nz = ux;
  const endpoint = which === 'from' ? seg.from : seg.to;
  const dir = which === 'from' ? { x: -ux, z: -uz } : { x: ux, z: uz };
  let ext = halfWidth;
  if (polygon) {
    const distAlong = rayDistanceToPolygon(endpoint, dir, polygon);
    if (distAlong !== null && distAlong < ext)
      ext = Math.max(0, distAlong - 1e-4);
    ext = maxExtensionWithPerpFit(
      endpoint,
      dir,
      { x: nx, z: nz },
      halfWidth,
      ext,
      polygon
    );
  }
  return {
    point: {
      x: endpoint.x + ext * dir.x,
      z: endpoint.z + ext * dir.z,
    },
    ext,
  };
}

/**
 * Build a 2-leg L-shape that bridges from `variantPoint` (variant's
 * truncated endpoint) to `defaultPoint` (where the default's
 * extension would have ended). The L-shape lies in the plane of the
 * original segment: one leg perpendicular to the segment direction,
 * the other along it.
 *
 * The order of legs is: perpendicular leg first (covers the lateral
 * shift between variant and default centerline), then along-segment
 * leg (covers the truncated length difference). Each leg is a
 * 2×halfWidth-wide rectangle.
 *
 * Returns `{ segments, rectangles }` arrays. Empty when the two
 * points coincide.
 */
function buildTransitionLLeg(variantPoint, defaultPoint, segDir, halfWidth) {
  const TRANSITION_LEG_MIN = 0.05; // 2" — below this the gap isn't visually meaningful
  const segments = [];
  const rectangles = [];
  // Project the offset between the two points into seg-along and
  // seg-perpendicular components.
  const ux = segDir.x;
  const uz = segDir.z;
  const nx = -uz;
  const nz = ux;
  const dx = defaultPoint.x - variantPoint.x;
  const dz = defaultPoint.z - variantPoint.z;
  const along = dx * ux + dz * uz;
  const perp = dx * nx + dz * nz;
  if (
    Math.abs(along) < TRANSITION_LEG_MIN &&
    Math.abs(perp) < TRANSITION_LEG_MIN
  ) {
    return { segments: [], rectangles: [] };
  }
  // Perpendicular leg: from variantPoint to corner = variantPoint + perp * normal.
  const cornerX = variantPoint.x + perp * nx;
  const cornerZ = variantPoint.z + perp * nz;
  const corner = { x: cornerX, z: cornerZ };
  if (Math.abs(perp) > TRANSITION_LEG_MIN) {
    const segPerp = { from: variantPoint, to: corner, axisIndex: -1 };
    // Rectangle: perpendicular leg is along ±normal; perpendicular to
    // it is along ±segDir.
    const rect = [
      {
        x: variantPoint.x - ux * halfWidth,
        z: variantPoint.z - uz * halfWidth,
      },
      { x: corner.x - ux * halfWidth, z: corner.z - uz * halfWidth },
      { x: corner.x + ux * halfWidth, z: corner.z + uz * halfWidth },
      {
        x: variantPoint.x + ux * halfWidth,
        z: variantPoint.z + uz * halfWidth,
      },
    ];
    segments.push(segPerp);
    rectangles.push(rect);
  }
  // Along leg: from corner to defaultPoint, along segDir.
  if (Math.abs(along) > TRANSITION_LEG_MIN) {
    const segAlong = { from: corner, to: defaultPoint, axisIndex: -1 };
    const rect = [
      { x: corner.x - nx * halfWidth, z: corner.z - nz * halfWidth },
      {
        x: defaultPoint.x - nx * halfWidth,
        z: defaultPoint.z - nz * halfWidth,
      },
      {
        x: defaultPoint.x + nx * halfWidth,
        z: defaultPoint.z + nz * halfWidth,
      },
      { x: corner.x + nx * halfWidth, z: corner.z + nz * halfWidth },
    ];
    segments.push(segAlong);
    rectangles.push(rect);
  }
  return { segments, rectangles };
}

const NEAR_WALL_PARALLEL_TOL_DEG = 5;
const NEAR_WALL_INNER_EDGE_THRESHOLD = 24 * 0.0254; // wall-side edge < 24" → close-to-wall
const SNAP_CENTERLINE_DISTANCE = 18 * 0.0254; // centerline 18" from wall (wall-side edge at 0)
const OFFSET_CENTERLINE_DISTANCE = 42 * 0.0254; // centerline 42" from wall (wall-side edge at 24" → 24" cabinet band)
// How far a shifted corridor may sit on or past the room outline and still
// count as inside it. A `snap` band lies ON its wall by design, and a real
// wall is never exactly square: 2425 San Fernando Ct's kitchen wall is 0.03 mm
// out over 20', which put the band's far corner 0.007 mm outside. Both checks
// below — corners inside, no edge crossing — read this one tolerance.
const BOUNDARY_TOL = 0.001; // 1 mm

/**
 * Build a rectangle from a (from, to) centerline pair plus a half-width
 * normal direction. Returns 4 corners in CCW order matching the
 * convention used elsewhere in this module.
 */
function rectFromCenterline(from, to, nx, nz, halfWidth) {
  return [
    { x: from.x - nx * halfWidth, z: from.z - nz * halfWidth },
    { x: to.x - nx * halfWidth, z: to.z - nz * halfWidth },
    { x: to.x + nx * halfWidth, z: to.z + nz * halfWidth },
    { x: from.x + nx * halfWidth, z: from.z + nz * halfWidth },
  ];
}

/**
 * Classify a corridor segment against a wall:
 *   - distance: unsigned perpendicular distance from wall to segment
 *     centerline (always non-negative).
 *   - refNormal: the wall's reference perpendicular unit vector,
 *     computed as `(-wallDz, wallDx) / wallLen`. Could point either
 *     side of the wall depending on the wall's stored
 *     bottomLeft/bottomRight winding — DO NOT assume it points into
 *     the kitchen.
 *   - side: ±1, the SIGN of the corridor's centerline along
 *     refNormal. The corridor is on the `side * refNormal` side of
 *     the wall.
 *   - segDir: segment unit direction.
 *
 * Returns `null` if the segment isn't parallel-within-tolerance to
 * the wall.
 */
function classifySegmentVsWall(seg, wall) {
  const a = wall?.alignment?.bottomLeft;
  const b = wall?.alignment?.bottomRight;
  if (!a || !b) return null;
  const wdx = b.x - a.x;
  const wdz = b.z - a.z;
  const wlen = Math.hypot(wdx, wdz);
  if (wlen < 1e-6) return null;
  const wux = wdx / wlen;
  const wuz = wdz / wlen;
  // Reference perpendicular (left of wall direction). The actual
  // kitchen-side normal may match this OR its negative — we figure
  // that out from the corridor's own signed perpendicular below.
  const refNx = -wuz;
  const refNz = wux;

  const sdx = seg.to.x - seg.from.x;
  const sdz = seg.to.z - seg.from.z;
  const slen = Math.hypot(sdx, sdz);
  if (slen < 1e-6) return null;
  const sux = sdx / slen;
  const suz = sdz / slen;

  const parallelDot = Math.abs(sux * wux + suz * wuz);
  const tolCos = Math.cos((NEAR_WALL_PARALLEL_TOL_DEG * Math.PI) / 180);
  if (parallelDot < tolCos) return null;

  // Wall-extent overlap: segment's projection onto the wall direction
  // must overlap the wall's [0, wlen] parametric range. A parallel
  // wall that's far past either segment endpoint isn't "alongside"
  // the segment at all — it just happens to be parallel.
  const sFromWall = (seg.from.x - a.x) * wux + (seg.from.z - a.z) * wuz;
  const sToWall = (seg.to.x - a.x) * wux + (seg.to.z - a.z) * wuz;
  const segMinAlongWall = Math.min(sFromWall, sToWall);
  const segMaxAlongWall = Math.max(sFromWall, sToWall);
  // Allow a small slack so segments meeting at a wall corner aren't
  // rejected by floating-point noise.
  const slack = 1e-3;
  if (segMaxAlongWall <= -slack) return null;
  if (segMinAlongWall >= wlen + slack) return null;

  const midX = (seg.from.x + seg.to.x) / 2;
  const midZ = (seg.from.z + seg.to.z) / 2;
  const signedPerp = (midX - a.x) * refNx + (midZ - a.z) * refNz;
  const distance = Math.abs(signedPerp);
  const side = signedPerp >= 0 ? 1 : -1;
  return {
    distance,
    refNormal: { x: refNx, z: refNz },
    side,
    segDir: { x: sux, z: suz },
  };
}

/**
 * Given a rectified walkway and the kitchen walls, produce three
 * variants of the corridor map:
 *   - 'default' — original rectified output.
 *   - 'snap'    — every close-to-wall corridor's centerline shifted to
 *                 SNAP_CENTERLINE_DISTANCE from its wall (wall-side edge
 *                 at the wall surface).
 *   - 'offset'  — every close-to-wall corridor's centerline shifted to
 *                 OFFSET_CENTERLINE_DISTANCE from its wall (wall-side
 *                 edge 24" from wall, leaving a 24" cabinet band).
 *
 * "Close to wall" means: the segment is parallel within 5° AND its
 * wall-side edge is currently < 24" from the wall (i.e. signed-perp
 * centerline < 42").
 *
 * @param {{rectangles: Array, segments: Array, primaryAxes: any}} rectified
 * @param {Array<Object>} walls - kitchen-section walls
 * @param {{halfWidth?: number}} [options]
 * @returns {Array<{label: string, segments: Array, rectangles: Array, primaryAxes: any}>}
 */
function generateWalkwayVariants(rectified, walls, options = {}) {
  const halfWidth = options.halfWidth ?? DEFAULT_HALF_WIDTH;
  const polygon = options.polygon || null;
  const obstacles = options.obstacles || [];
  const primaryAxes = rectified?.primaryAxes || null;

  // edgeOptions is the authoritative source for all L-orientation choices.
  // Fall back to wrapping the default segments/rectangles as a single option
  // per edge when edgeOptions is absent (backwards-compat with old callers).
  const edgeOptions = rectified?.edgeOptions || null;
  const defaultSegments = rectified?.segments || [];
  const defaultRectangles = rectified?.rectangles || [];

  // Returns per-segment classification vs walls: the closest wall each
  // segment is parallel to and whose wall-side edge is < NEAR_WALL_INNER_EDGE_THRESHOLD.
  const classify = (segs) =>
    segs.map((seg) => {
      let best = null;
      for (const wall of walls || []) {
        const cls = classifySegmentVsWall(seg, wall);
        if (!cls) continue;
        const wallSideEdge = cls.distance - halfWidth;
        if (wallSideEdge >= NEAR_WALL_INNER_EDGE_THRESHOLD) continue;
        if (best === null || cls.distance < best.distance) best = cls;
      }
      return best;
    });

  // Signed distance from p to the line through a→b (0 for a degenerate line).
  const sideOfLine = (a, b, p) => {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-12) return 0;
    return ((p.x - a.x) * dz - (p.z - a.z) * dx) / len;
  };
  const straddles = (s1, s2) =>
    (s1 > BOUNDARY_TOL && s2 < -BOUNDARY_TOL) ||
    (s1 < -BOUNDARY_TOL && s2 > BOUNDARY_TOL);

  // True when segments (p1,p2) and (p3,p4) really CROSS: each one's ends lie
  // on opposite sides of the other's line by more than BOUNDARY_TOL. An end
  // within that distance of the other line is touching, not crossing — which
  // also covers shared endpoints and collinear contact. Measured in metres,
  // not as a fraction of the edge, so a long band is held to the same 1 mm as
  // a short one.
  const segmentsIntersect = (p1, p2, p3, p4) =>
    straddles(sideOfLine(p3, p4, p1), sideOfLine(p3, p4, p2)) &&
    straddles(sideOfLine(p1, p2, p3), sideOfLine(p1, p2, p4));

  // Returns true if any edge of rect (4 corners CCW or CW) crosses any polygon edge.
  const rectCrossesPolygonEdge = (rect, poly) => {
    if (!poly || poly.length < 3) return false;
    for (let i = 0; i < 4; i++) {
      const r1 = rect[i];
      const r2 = rect[(i + 1) % 4];
      for (let j = 0; j < poly.length; j++) {
        const p1 = poly[j];
        const p2 = poly[(j + 1) % poly.length];
        if (segmentsIntersect(r1, r2, p1, p2)) return true;
      }
    }
    return false;
  };

  const segmentLength = (s) => {
    const dx = s.to.x - s.from.x;
    const dz = s.to.z - s.from.z;
    return Math.hypot(dx, dz);
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

  const shiftSegmentEndpoints = (seg, dx, dz) => ({
    ...seg,
    from: { x: seg.from.x + dx, z: seg.from.z + dz },
    to: { x: seg.to.x + dx, z: seg.to.z + dz },
  });

  // Build one variant given materialized segments+rectangles and a target centerline.
  const buildVariant = (label, targetCenterline, comboSegs, comboRects) => {
    const segmentClassification = classify(comboSegs);
    const outSegments = comboSegs.map((s) => s);
    const outRectangles = comboRects.map((r) => r);

    if (targetCenterline == null) {
      // Default variant — no shifts
      const merged = mergeCollinearRectangles(outRectangles);
      const alongWall = merged.map(() => false);
      return {
        label,
        segments: outSegments,
        rectangles: merged,
        alongWall,
        primaryAxes,
      };
    }

    // Group segment indices by edgeIndex.
    const groups = new Map();
    for (let i = 0; i < comboSegs.length; i++) {
      const ei = comboSegs[i].edgeIndex ?? -1;
      if (!groups.has(ei)) groups.set(ei, []);
      groups.get(ei).push(i);
    }

    // Pick the primary index in a group: classified one if only one is, else
    // longer of two classified, else null.
    const pickPrimary = (indices) => {
      if (indices.length === 1) {
        return segmentClassification[indices[0]] ? indices[0] : null;
      }
      const classified = indices.filter((i) => segmentClassification[i]);
      if (classified.length === 0) return null;
      if (classified.length === 1) return classified[0];
      classified.sort(
        (a, b) => segmentLength(comboSegs[b]) - segmentLength(comboSegs[a])
      );
      return classified[0];
    };

    const alongWallByOriginalIndex = new Array(comboSegs.length).fill(false);

    for (const [, indices] of groups) {
      const primaryIdx = pickPrimary(indices);
      if (primaryIdx == null) continue;
      const cls = segmentClassification[primaryIdx];
      const currentSigned = cls.side * cls.distance;
      const targetSigned = cls.side * targetCenterline;
      const delta = targetSigned - currentSigned;
      if (Math.abs(delta) < 1e-9) continue;
      const shiftDx = delta * cls.refNormal.x;
      const shiftDz = delta * cls.refNormal.z;

      // Shift primary fully.
      const primarySeg = comboSegs[primaryIdx];
      const shiftedPrimarySeg = shiftSegmentEndpoints(
        primarySeg,
        shiftDx,
        shiftDz
      );
      const shiftedPrimaryRect = segmentToRectangle(
        shiftedPrimarySeg,
        halfWidth,
        polygon
      );
      if (!shiftedPrimaryRect) continue;
      // Validate primary stays in polygon (with 1mm shrink for boundary tol).
      if (polygon) {
        const cx =
          (shiftedPrimaryRect[0].x +
            shiftedPrimaryRect[1].x +
            shiftedPrimaryRect[2].x +
            shiftedPrimaryRect[3].x) /
          4;
        const cz =
          (shiftedPrimaryRect[0].z +
            shiftedPrimaryRect[1].z +
            shiftedPrimaryRect[2].z +
            shiftedPrimaryRect[3].z) /
          4;
        const SHRINK = BOUNDARY_TOL;
        const allInside = shiftedPrimaryRect.every((p) => {
          const len = Math.hypot(cx - p.x, cz - p.z);
          if (len < 1e-9) return isPointInsidePolygon(p, polygon);
          const probe = {
            x: p.x + ((cx - p.x) / len) * SHRINK,
            z: p.z + ((cz - p.z) / len) * SHRINK,
          };
          return isPointInsidePolygon(probe, polygon);
        });
        if (!allInside) continue;
        if (rectCrossesPolygonEdge(shiftedPrimaryRect, polygon)) continue;
      }
      // Never shift a band into a counter.
      if (hitsObstacle([shiftedPrimaryRect], obstacles)) continue;
      outSegments[primaryIdx] = shiftedPrimarySeg;
      outRectangles[primaryIdx] = shiftedPrimaryRect;
      alongWallByOriginalIndex[primaryIdx] = true;

      // Update secondary segment(s) — their connecting endpoint follows
      // the primary's shifted corner. Match endpoints by minimum distance.
      for (const idx of indices) {
        if (idx === primaryIdx) continue;
        const secSeg = comboSegs[idx];
        // Identify which secondary endpoint matches which primary endpoint
        // (within a tolerance). Update that endpoint to the SHIFTED primary endpoint.
        const dPrimaryFromSecFrom = dist(primarySeg.from, secSeg.from);
        const dPrimaryFromSecTo = dist(primarySeg.from, secSeg.to);
        const dPrimaryToSecFrom = dist(primarySeg.to, secSeg.from);
        const dPrimaryToSecTo = dist(primarySeg.to, secSeg.to);
        const minDist = Math.min(
          dPrimaryFromSecFrom,
          dPrimaryFromSecTo,
          dPrimaryToSecFrom,
          dPrimaryToSecTo
        );
        const TOUCH_TOL = 0.01;
        if (minDist > TOUCH_TOL) continue; // secondary doesn't actually share a corner
        let newSecSeg = {
          ...secSeg,
          from: { ...secSeg.from },
          to: { ...secSeg.to },
        };
        if (minDist === dPrimaryFromSecFrom) {
          newSecSeg.from = {
            x: shiftedPrimarySeg.from.x,
            z: shiftedPrimarySeg.from.z,
          };
        } else if (minDist === dPrimaryFromSecTo) {
          newSecSeg.to = {
            x: shiftedPrimarySeg.from.x,
            z: shiftedPrimarySeg.from.z,
          };
        } else if (minDist === dPrimaryToSecFrom) {
          newSecSeg.from = {
            x: shiftedPrimarySeg.to.x,
            z: shiftedPrimarySeg.to.z,
          };
        } else {
          newSecSeg.to = {
            x: shiftedPrimarySeg.to.x,
            z: shiftedPrimarySeg.to.z,
          };
        }
        // Re-rectify the adjusted secondary.
        const newRect = segmentToRectangle(newSecSeg, halfWidth, polygon);
        if (!newRect) continue;
        if (polygon && rectCrossesPolygonEdge(newRect, polygon)) continue;
        outSegments[idx] = newSecSeg;
        outRectangles[idx] = newRect;
        // Secondary stays "along wall" only if it was originally classified —
        // it may have been a wall-tracker too, otherwise it's just an L-leg
        // adjusted to follow primary. Mark conservatively.
        alongWallByOriginalIndex[idx] = !!segmentClassification[idx];
      }
    }

    const merged = mergeCollinearRectangles(outRectangles);
    // Re-derive alongWall after merge: a merged rect inherits alongWall=true if
    // any source rect within it was alongWall.
    const alongWall = merged.map((mergedRect) => {
      const ms = aabbOf(mergedRect);
      for (let i = 0; i < outRectangles.length; i++) {
        if (!alongWallByOriginalIndex[i]) continue;
        const a = aabbOf(outRectangles[i]);
        if (
          a.minX >= ms.minX - 1e-3 &&
          a.maxX <= ms.maxX + 1e-3 &&
          a.minZ >= ms.minZ - 1e-3 &&
          a.maxZ <= ms.maxZ + 1e-3
        ) {
          return true;
        }
      }
      return false;
    });

    return {
      label,
      segments: outSegments,
      rectangles: merged,
      alongWall,
      primaryAxes,
    };
  };

  // Materialize segments + rectangles for a given L-orientation combo.
  // combo is a map: edgeIndex → optionIndex (0 = primary, 1 = alternate).
  const materialize = (combo) => {
    if (!edgeOptions) {
      // No edgeOptions — use the default segments/rectangles directly.
      return { segments: defaultSegments, rectangles: defaultRectangles };
    }
    const segs = [];
    const rects = [];
    for (let i = 0; i < edgeOptions.length; i++) {
      const opts = edgeOptions[i];
      if (!opts || opts.length === 0) continue;
      const idx = combo[i] != null ? combo[i] : 0;
      const opt = opts[idx] || opts[0];
      if (!opt) continue;
      for (let k = 0; k < opt.segments.length; k++) {
        segs.push({ ...opt.segments[k], edgeIndex: i, optionIndex: idx });
        rects.push(opt.rectangles[k]);
      }
    }
    return { segments: segs, rectangles: rects };
  };

  // Compute which edge indices have 2+ valid L-options.
  const nonAlignedEdgeIndices = edgeOptions
    ? edgeOptions.reduce((acc, opts, i) => {
        if (opts && opts.length >= 2) acc.push(i);
        return acc;
      }, [])
    : [];

  // Build the list of L-orientation combos to enumerate.
  const N = nonAlignedEdgeIndices.length;
  let combos;
  if (N === 0) {
    combos = [{}]; // single combo, no choices
  } else if (N <= 2) {
    // Enumerate all 2^N combinations.
    combos = [];
    for (let mask = 0; mask < 1 << N; mask++) {
      const combo = {};
      for (let bit = 0; bit < N; bit++) {
        combo[nonAlignedEdgeIndices[bit]] = (mask >> bit) & 1;
      }
      combos.push(combo);
    }
  } else {
    // Cap: only all-primary and all-alternate combinations.
    const allPrimary = {};
    const allAlternate = {};
    for (const ei of nonAlignedEdgeIndices) {
      allPrimary[ei] = 0;
      allAlternate[ei] = 1;
    }
    combos = [allPrimary, allAlternate];
  }

  const result = [];
  for (let c = 0; c < combos.length; c++) {
    const combo = combos[c];
    const { segments: comboSegs, rectangles: comboRects } = materialize(combo);
    const suffix = combos.length > 1 ? `-L${c}` : '';
    result.push(buildVariant(`default${suffix}`, null, comboSegs, comboRects));
    result.push(
      buildVariant(
        `snap${suffix}`,
        SNAP_CENTERLINE_DISTANCE,
        comboSegs,
        comboRects
      )
    );
    result.push(
      buildVariant(
        `offset${suffix}`,
        OFFSET_CENTERLINE_DISTANCE,
        comboSegs,
        comboRects
      )
    );
  }
  return result;
}

const MERGE_TOL = 0.005; // 5mm — tolerance for "shared edge / same extent"

/**
 * Merge any two rectangles in `rectangles` that share a full edge
 * and have the same perpendicular extent — i.e., they're adjacent
 * and collinear along one primary axis. Returns a new array with
 * merged rectangles. Iterates until no more merges are possible.
 *
 * Each rectangle must be 4 corners in CCW order. Works for rectangles
 * aligned to either primary axis (X-aligned or Z-aligned).
 *
 * Pure: no side effects.
 */
/**
 * Compute axis-aligned bounding box of a rectangle.
 */
function aabbOf(rect) {
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (const p of rect) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minZ, maxZ };
}

function mergeCollinearRectangles(rectangles) {
  // Walkway rectangles are USUALLY axis-aligned, but the "last-resort
  // slanted" branch in rectifyWalkways emits rotated rectangles for MST
  // edges that no L-shape could fit. Those must be preserved as-is —
  // converting them to an AABB would blow them up into a much larger
  // axis-aligned bounding box that covers the slant's whole envelope.
  // Sort each rectangle into one of two buckets, merge only the
  // axis-aligned ones, and pass the slanted ones through unchanged.
  const isAxisAligned = (rect) => {
    if (!rect || rect.length !== 4) return false;
    const xs = new Set(rect.map((p) => Math.round(p.x * 1e6)));
    const zs = new Set(rect.map((p) => Math.round(p.z * 1e6)));
    return xs.size === 2 && zs.size === 2;
  };

  const aaRects = [];
  const passthrough = [];
  for (const rect of rectangles) {
    if (isAxisAligned(rect)) aaRects.push(rect);
    else passthrough.push(rect);
  }

  // Convert each rectangle to a normalized AABB form: {minX, maxX,
  // minZ, maxZ}.
  const toAabb = (rect) => aabbOf(rect);
  const fromAabb = (a) => [
    { x: a.minX, z: a.minZ },
    { x: a.maxX, z: a.minZ },
    { x: a.maxX, z: a.maxZ },
    { x: a.minX, z: a.maxZ },
  ];
  const eq = (a, b) => Math.abs(a - b) < MERGE_TOL;

  let aabbs = aaRects.map(toAabb);
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < aabbs.length; i++) {
      for (let j = i + 1; j < aabbs.length; j++) {
        const A = aabbs[i];
        const B = aabbs[j];
        // Same X extent + adjacent in Z?
        if (eq(A.minX, B.minX) && eq(A.maxX, B.maxX)) {
          if (eq(A.maxZ, B.minZ)) {
            aabbs[i] = {
              minX: A.minX,
              maxX: A.maxX,
              minZ: A.minZ,
              maxZ: B.maxZ,
            };
            aabbs.splice(j, 1);
            changed = true;
            break outer;
          }
          if (eq(B.maxZ, A.minZ)) {
            aabbs[i] = {
              minX: A.minX,
              maxX: A.maxX,
              minZ: B.minZ,
              maxZ: A.maxZ,
            };
            aabbs.splice(j, 1);
            changed = true;
            break outer;
          }
        }
        // Same Z extent + adjacent in X?
        if (eq(A.minZ, B.minZ) && eq(A.maxZ, B.maxZ)) {
          if (eq(A.maxX, B.minX)) {
            aabbs[i] = {
              minX: A.minX,
              maxX: B.maxX,
              minZ: A.minZ,
              maxZ: A.maxZ,
            };
            aabbs.splice(j, 1);
            changed = true;
            break outer;
          }
          if (eq(B.maxX, A.minX)) {
            aabbs[i] = {
              minX: B.minX,
              maxX: A.maxX,
              minZ: A.minZ,
              maxZ: A.maxZ,
            };
            aabbs.splice(j, 1);
            changed = true;
            break outer;
          }
        }
      }
    }
  }

  return [...aabbs.map(fromAabb), ...passthrough];
}

module.exports = {
  computePrimaryAxes,
  rectifyEdge,
  // Also the ray Extend to wall casts (room-split/extend.js).
  rayDistanceToPolygon,
  segmentToRectangle,
  rectifyWalkways,
  generateWalkwayVariants,
  mergeCollinearRectangles,
  DEFAULT_HALF_WIDTH,
  DEFAULT_SNAP_TOLERANCE_DEG,
};
